import React from 'react';
import { TagTypes } from '../../../Api/Types/tagTypes';
import { bankDocumentsApi } from '../Api/bankDocumentsApi';
import { normalizePositiveInteger } from './lookupPagination';
import {
  mapBookingToBankDocumentLookup,
  type BankDocumentBookingLookup,
  type BankDocumentBookingLookupSource,
} from './bookingsLookupMapper';

interface BankDocumentBookingLookupArgs {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  companyId?: number | null;
}

interface BankDocumentBookingsLookupRequest {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  companyId?: number | null;
  dateFilterMode: 'startDate';
}

interface BankDocumentBookingsPagedResponse {
  items?: BankDocumentBookingLookupSource[] | null;
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
}

export const buildBankDocumentBookingsLookupUrl = (
  request: BankDocumentBookingsLookupRequest
): string => {
  const queryParams = new URLSearchParams();
  const normalizedSearchTerm = request.searchTerm?.trim();
  const normalizedPageNumber = normalizePositiveInteger(request.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(request.pageSize, 10);

  if (normalizedSearchTerm) {
    queryParams.set('searchTerm', normalizedSearchTerm);
  }

  if (request.companyId !== null && request.companyId !== undefined) {
    queryParams.set('companyId', request.companyId.toString());
  }

  queryParams.set('dateFilterMode', request.dateFilterMode);
  queryParams.set('pageNumber', normalizedPageNumber.toString());
  queryParams.set('pageSize', normalizedPageSize.toString());

  return `/bookings/search-v2?${queryParams.toString()}`;
};

const bankDocumentBookingsLookupApi = bankDocumentsApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankDocumentBookingsLookupRaw: builder.query<
      BankDocumentBookingsPagedResponse,
      BankDocumentBookingsLookupRequest
    >({
      query: buildBankDocumentBookingsLookupUrl,
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
  }),
});

const { useGetBankDocumentBookingsLookupRawQuery } = bankDocumentBookingsLookupApi;

type BookingsLookupQueryOptions = Parameters<typeof useGetBankDocumentBookingsLookupRawQuery>[1];
type BookingsLookupQueryResult = ReturnType<typeof useGetBankDocumentBookingsLookupRawQuery>;
type BankDocumentBookingsLookupQueryResult = Pick<
  BookingsLookupQueryResult,
  'isLoading' | 'isFetching' | 'isError' | 'error' | 'refetch'
> & {
  data: BankDocumentBookingLookup[];
  pagination: {
    totalCount: number;
    pageNumber: number;
    pageSize: number;
  };
};

export const useGetBankDocumentBookingsLookupQuery = (
  args: BankDocumentBookingLookupArgs,
  options?: BookingsLookupQueryOptions
): BankDocumentBookingsLookupQueryResult => {
  const queryOptions: BookingsLookupQueryOptions = options;
  const normalizedSearchTerm = args.searchTerm?.trim() || undefined;
  const normalizedPageNumber = normalizePositiveInteger(args.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(args.pageSize, 10);

  const queryResult = useGetBankDocumentBookingsLookupRawQuery(
    {
      searchTerm: normalizedSearchTerm,
      pageNumber: normalizedPageNumber,
      pageSize: normalizedPageSize,
      companyId: args.companyId ?? null,
      dateFilterMode: 'startDate',
    },
    queryOptions
  );

  const pagedResponse = queryResult.data;

  const data = React.useMemo(
    () =>
      (pagedResponse?.items ?? [])
        .map((booking: BankDocumentBookingLookupSource) => mapBookingToBankDocumentLookup(booking))
        .filter((item: BankDocumentBookingLookup | undefined): item is BankDocumentBookingLookup =>
          Boolean(item)
        ),
    [pagedResponse]
  );

  return {
    data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
    pagination: {
      totalCount: pagedResponse?.totalCount ?? 0,
      pageNumber: pagedResponse?.pageNumber ?? normalizedPageNumber,
      pageSize: pagedResponse?.pageSize ?? normalizedPageSize,
    },
  };
};